import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { action } from "typesafe-actions";
import { api } from "../../api/api";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { IStoreState } from "../initialStoreState";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import {
  IReviewsQuestionnaire,
  IReviewsQuestionnaireUpsertPayload,
} from "./ReviewQuestionnaire.types";

export const FETCH_REVIEWS_QUESTIONNAIRE_LIST_PROGRESS =
  "FETCH_REVIEWS_QUESTIONNAIRE_LIST_PROGRESS";
export const FETCH_REVIEWS_QUESTIONNAIRE_LIST_SUCCESS =
  "FETCH_REVIEWS_QUESTIONNAIRE_LIST_SUCCESS";
export const FETCH_REVIEWS_QUESTIONNAIRE_LIST_FAILED =
  "FETCH_REVIEWS_QUESTIONNAIRE_LIST_FAILED";

export const fetchReviewsQuestionnaireListProgress = () =>
  action(FETCH_REVIEWS_QUESTIONNAIRE_LIST_PROGRESS);
export const fetchReviewsQuestionnaireListSuccess = (
  data: IReviewsQuestionnaire[],
  totalRecords: number,
  currentRecords: number,
) =>
  action(FETCH_REVIEWS_QUESTIONNAIRE_LIST_SUCCESS, {
    data,
    totalRecords,
    currentRecords,
  });
export const fetchReviewsQuestionnaireListFailed = () =>
  action(FETCH_REVIEWS_QUESTIONNAIRE_LIST_FAILED);

export const fetchReviewsQuestionnaireListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    searchValue?: IDatatableV2AdvancedSearchFilter | null,
    company_outlet_uuid?: string,
    status?: string,
    rating?: number | null,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        dispatch(fetchReviewsQuestionnaireListProgress());
        dispatch(saveLoaderProgress());

        let url = `/review/get-reviews-questionnaire?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}`;

        // if (company_outlet_uuid) {
        //   url += `&company_outlet_uuid=${company_outlet_uuid}`;
        // }

        if (searchValue && searchValue.length > 0) {
          url += `&advanceFilter=${JSON.stringify(searchValue)}`;
        }

        if (status) {
          url += `&status=${status}`;
        }

        if (
          rating != null &&
          Number.isFinite(rating) &&
          rating >= 1 &&
          rating <= 5
        ) {
          url += `&rating=${rating}`;
        }

        const response = await api.get(url);

        if (response.status === 200 && response.data) {
          const data: IReviewsQuestionnaire[] = Array.isArray(
            response.data.data,
          )
            ? response.data.data
            : [];

          const totalRecords =
            typeof response.data.totalRecords === "number"
              ? response.data.totalRecords
              : data.length;
          const currentRecords =
            typeof response.data.currentRecords === "number"
              ? response.data.currentRecords
              : data.length;

          dispatch(
            fetchReviewsQuestionnaireListSuccess(
              data,
              totalRecords,
              currentRecords,
            ),
          );
        }
      } catch (err: any) {
        dispatch(fetchReviewsQuestionnaireListFailed());
        dispatch(
          showMessage({
            type: "error",
            message:
              err.response?.data?.message ||
              "Failed to fetch reviews questionnaire",
            displayAs: "snackbar",
          }),
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const FETCH_REVIEWS_QUESTIONNAIRE_PROGRESS =
  "FETCH_REVIEWS_QUESTIONNAIRE_PROGRESS";
export const FETCH_REVIEWS_QUESTIONNAIRE_SUCCESS =
  "FETCH_REVIEWS_QUESTIONNAIRE_SUCCESS";
export const FETCH_REVIEWS_QUESTIONNAIRE_FAILED =
  "FETCH_REVIEWS_QUESTIONNAIRE_FAILED";

export const fetchReviewsQuestionnaireProgress = () =>
  action(FETCH_REVIEWS_QUESTIONNAIRE_PROGRESS);
export const fetchReviewsQuestionnaireSuccess = (data: IReviewsQuestionnaire) =>
  action(FETCH_REVIEWS_QUESTIONNAIRE_SUCCESS, { data });
export const fetchReviewsQuestionnaireFailed = (errorMessage: string) =>
  action(FETCH_REVIEWS_QUESTIONNAIRE_FAILED, { errorMessage });

export const fetchReviewsQuestionnaireAsync =
  (
    questionnaireUuid: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        dispatch(fetchReviewsQuestionnaireProgress());
        dispatch(saveLoaderProgress());

        const url = `/review/get-reviews-questionnaire?pageNo=1&itemPerPage=10&reviews_questionnaire_uuid=${questionnaireUuid}`;
        const res = await api.get(url);
        const data: IReviewsQuestionnaire[] = Array.isArray(res.data?.data)
          ? res.data.data
          : [];

        if (data.length > 0) {
          dispatch(fetchReviewsQuestionnaireSuccess(data[0]));
        } else {
          dispatch(
            fetchReviewsQuestionnaireFailed(
              "No questionnaire found for the given id.",
            ),
          );
        }
      } catch (err: any) {
        dispatch(fetchReviewsQuestionnaireFailed(""));
        dispatch(
          showMessage({
            type: "error",
            message:
              err.response?.data?.message ||
              "Failed to fetch reviews questionnaire",
            displayAs: "snackbar",
          }),
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

function buildUpsertPayload(
  entity: IReviewsQuestionnaire,
): IReviewsQuestionnaireUpsertPayload {
  return {
    reviews_questionnaire_uuid: entity.reviews_questionnaire_uuid,
    rating: entity.rating,
    questions: entity.questions.map((q) => ({
      question: q.question,
      question_type: q.question_type,
      options: [...q.options],
    })),
    remarks: entity.remarks,
    company_outlet_uuid: entity.company_outlet_uuid,
    status: entity.status,
  };
}

export const upsertReviewsQuestionnaireAsync =
  (
    entity: IReviewsQuestionnaire,
    onCallBack: (isSuccess?: boolean, data?: IReviewsQuestionnaire) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      dispatch(saveLoaderProgress());
      try {
        const payload = buildUpsertPayload(entity);
        const res = await api.post(
          "/review/upsert-reviews-questionnaire",
          payload,
        );

        const data = res.data?.data as IReviewsQuestionnaire | undefined;
        onCallBack(true, data);
      } catch (err: any) {
        onCallBack(false);

        let errorMessage = "An error occurred";
        if (err.response?.data) {
          if (
            err.response.data.message === 403 &&
            err.response.data.data
          ) {
            errorMessage = err.response.data.data;
          } else if (err.response.data.message) {
            errorMessage = err.response.data.message;
          } else if (err.response.data.error) {
            errorMessage = err.response.data.error;
          }
        }

        dispatch(
          showMessage({
            type: "error",
            message: errorMessage,
            displayAs: "snackbar",
          }),
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const CLEAR_REVIEWS_QUESTIONNAIRE_STATE =
  "CLEAR_REVIEWS_QUESTIONNAIRE_STATE";
export const CLEAR_REVIEWS_QUESTIONNAIRE = "CLEAR_REVIEWS_QUESTIONNAIRE";

export const clearReviewsQuestionnaireState = () =>
  action(CLEAR_REVIEWS_QUESTIONNAIRE_STATE);
export const clearReviewsQuestionnaire = () =>
  action(CLEAR_REVIEWS_QUESTIONNAIRE);

export const GENERATE_REVIEW_QR_PROGRESS = "GENERATE_REVIEW_QR_PROGRESS";
export const GENERATE_REVIEW_QR_SUCCESS = "GENERATE_REVIEW_QR_SUCCESS";
export const GENERATE_REVIEW_QR_FAILED = "GENERATE_REVIEW_QR_FAILED";

export const generateReviewQrProgress = () => action(GENERATE_REVIEW_QR_PROGRESS);
export const generateReviewQrSuccess = (base64: string) =>
  action(GENERATE_REVIEW_QR_SUCCESS, { base64 });
export const generateReviewQrFailed = (errorMessage: string) =>
  action(GENERATE_REVIEW_QR_FAILED, { errorMessage });

export const generateReviewQrCodeAsync =
  (
    companyOutletUuid: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      const reviewUrl = `${window.location.origin}/public/review?company_id=${encodeURIComponent(companyOutletUuid)}`;

      try {
        dispatch(generateReviewQrProgress());
        dispatch(saveLoaderProgress());

        const res = await api.get("/review/generate-qr-code", {
          params: {
            url: reviewUrl,
          },
        });

        const raw =
          typeof res.data?.data === "string"
            ? res.data.data
            : typeof res.data === "string"
              ? res.data
              : "";

        if (raw) {
          dispatch(generateReviewQrSuccess(raw));
        } else {
          dispatch(
            generateReviewQrFailed("QR code was not returned by the server."),
          );
          dispatch(
            showMessage({
              type: "error",
              message: "QR code was not returned by the server.",
              displayAs: "snackbar",
            }),
          );
        }
      } catch (err: any) {
        dispatch(generateReviewQrFailed(""));
        dispatch(
          showMessage({
            type: "error",
            message:
              err.response?.data?.message ||
              "Failed to generate review QR code",
            displayAs: "snackbar",
          }),
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };
